import { isSupported } from '@archipro-website/localisation';
import { EnquiryAudienceEnum } from 'generated/graphql';
import type {
    ProfessionalDetailsInitialFragment,
    GetProfessionalHeaderSignalsQuery,
} from 'generated/graphql';
import type { Breadcrumb } from '@modules/professional/type/details-root.types';
import type { DropdownItem } from '~/modules/enquiry/types';
import type { Branch } from '~/modules/enquiry/util/extract-branch-from-network.server';
import { useMatches } from 'react-router';
import { useMemo } from 'react';

import {
    PROFESSIONAL_DETAILS_BASE_ROUTE_ID,
    PREVIEW_BASE_ROUTE_ID,
} from '@modules/business-manager/config/constants';
import type { PrimaryCTAResult } from '@modules/professional/util/professional-cta-helper';
import type { ProfessionalBannersData } from '@modules/professional/page/OverviewPage';
import type { ProfessionalDetailRootData } from '../page/ProfessionalPage';

export type ProfessionalInitialResult = ReturnType<
    typeof mapProfessionalInitialRouteData
>;

/** A single industry-association / certification body the professional belongs to. */
export type ProfessionalAssociation =
    GetProfessionalHeaderSignalsQuery['Associations']['nodes'][number];

/**
 * Header trust signals that live outside the main professional query — the
 * association bodies (with a count), the article count and the network count.
 * Loaded once in the root loader so the persistent header renders them on every
 * tab. All optional/zeroed so a failed fetch degrades to "no signal".
 */
export interface ProfessionalHeaderSignals {
    associationCount: number;
    associations: ProfessionalAssociation[];
    articleCount: number;
    networkCount: number;
}

export const useProfessionalInitialRouteData = () => {
    const matchingRoutes = useMatches();

    const route = useMemo(() => {
        return matchingRoutes.find((route) => {
            return [
                PROFESSIONAL_DETAILS_BASE_ROUTE_ID,
                PREVIEW_BASE_ROUTE_ID,
            ].includes(route.id);
        });
    }, [matchingRoutes]);

    if (!route) {
        console.warn(
            'useProfessionalInitialRouteData must be used within a professional route',
        );
    }

    const {
        initial,
        banners,
        professionalBreadcrumb,
        branchOptions,
        primaryCTA,
        headerSignals,
    } = route!.data as ProfessionalDetailRootData;

    return mapProfessionalInitialRouteData(
        initial,
        banners,
        professionalBreadcrumb,
        branchOptions,
        primaryCTA,
        headerSignals,
    );
};

/*
![TECH DEBT] (exposed graphql) Loaders should not be returning raw graphql types/fragments.
 */
export const mapProfessionalInitialRouteData = <
    T extends ProfessionalDetailsInitialFragment,
>(
    obj: T,
    banners?: ProfessionalBannersData,
    breadcrumbs?: Breadcrumb[],
    branchOptions?: DropdownItem<Branch>[],
    primaryCTA?: PrimaryCTAResult,
    headerSignals?: ProfessionalHeaderSignals,
) => ({
    __typename: obj.__typename,
    ID: obj.ID,
    Link: obj.Link,
    BannerImages: obj.BannerImages,
    CompanyLogo: obj.CompanyLogo ?? '',
    LogoBackgroundColor: obj.LogoBackgroundColor ?? 'white',
    BaseUrl: `/professional/${obj.URLSegment}`,
    Title: obj.Title,
    PrimaryCategory: obj.PrimaryCategory,
    Categories: obj.Categories,
    Website: obj.Website ?? undefined,
    // Read by the profile summary header, which renders on every tab — so
    // these travel with the shared initial data rather than the overview
    // loader that previously owned them.
    BlockSummary: obj.BlockSummary ?? undefined,
    Services: obj.Services ?? [],
    SocialLinks: {
        facebook: obj.PublicFacebookLink ?? undefined,
        instagram: obj.PublicInstagramLink ?? undefined,
        linkedin: obj.PublicLinkedinLink ?? undefined,
        pinterest: obj.PublicPinterestLink ?? undefined,
        youtube: obj.PublicYoutubeLink ?? undefined,
    },
    CustomEnquiryMessage: obj.CustomEnquiryMessage ?? undefined,
    EnquiryMessageOverrideEnabled: obj.EnquiryMessageOverrideEnabled ?? false,
    EnquiryMessagePlaceholder: obj.EnquiryMessagePlaceholder ?? undefined,
    EnquiryAudience: obj.EnquiryAudience ?? EnquiryAudienceEnum.Anybody,
    EnquiryResponseRate: obj.EnquiryResponseRate,
    EnquiryAvgResponseHours: obj.EnquiryAvgResponseHours,
    ProfileLiveDate: obj.ProfileLiveDate,
    // Year the company was founded — member-entered, so a blank string means
    // "unset" and is normalised to null for the "in business N years" signal.
    YearFounded: obj.YearFounded || null,
    AggregatedPublicKPI: obj.AggregatedPublicKPI,
    Awards: obj.Awards,
    // Header trust signals — testimonial count from the fragment, the rest from
    // the sibling header-signals query threaded through the loader.
    TestimonialCount: obj.Testimonials?.length ?? 0,
    AssociationCount: headerSignals?.associationCount ?? 0,
    Associations: headerSignals?.associations ?? [],
    ArticleCount: headerSignals?.articleCount ?? 0,
    NetworkCount: headerSignals?.networkCount ?? 0,
    SamplesAvailable: obj.SamplesAvailable,
    PageType: obj.PageType,
    Country: isSupported(obj.Country) ? obj.Country : 'NZ',
    LibraryLocations: obj.LibraryLocations.filter((l) => !l.LocationHidden),
    PriceRanges: obj.PriceRanges ?? [],
    URLSegment: obj.URLSegment,
    IsManufacturer: obj.IsManufacturer,
    HasProducts: obj.HasProducts,
    ProjectCount: obj.ProjectCount,
    ProductCount: obj.ProductCount,
    HasProjects: obj.HasProjects,
    HasProjectsUsingThisProfessional: obj.HasProjectsUsingThisProfessional,
    HasVideos: obj.HasVideos,
    HasArticles: obj.HasArticles,
    HasPartners: obj.HasPartners,
    HasDownloads: obj.HasDownloads ?? false,
    HasImageLabels: obj.HasImageLabels ?? false,
    ProductsAllowed: obj.ProductsAllowed,
    Banners: banners ?? [],
    Breadcrumbs: breadcrumbs ?? [],
    branchOptions: branchOptions ?? [],
    ArchiproEditorialAccount: obj.ArchiproEditorialAccount,
    ShowOwnProjectsOnly: obj.ShowOwnProjectsOnly,
    BannerOverlay: obj.BannerOverlay,
    primaryCTA,
    AvailableForWork: obj.AvailableForWork,
    Content: obj.Content ?? '',
    AssociatedPages: obj.AssociatedPages,
    Phone: obj.Phone ?? undefined,
    PhoneTracking: obj.PhoneTracking ?? undefined,
    PrimaryAddress: obj.PrimaryAddress ?? undefined,
    BusinessFocus: obj.BusinessFocus ?? undefined,
    // The docked Book a Meeting CTA rides the tab band, so it needs the session
    // on every tab rather than only the one whose loader fetches the overview.
    ClientDirectorCalendarSession: obj.ClientDirectorCalendarSession ?? null,
});
